import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from 'react-daisyui';
import { Link } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import { sendResetPasswordLinkSchema, SendResetPasswordLinkRequest } from '$/requests/SendResetPasswordLink.ts';
import { TextField, Typography, Box, Stack } from '@mui/material';

const ForgotPasswordPage: FC = () => {
  const { t } = useTranslation();
  const { control, handleSubmit } = useForm<SendResetPasswordLinkRequest>({
    resolver: zodResolver(sendResetPasswordLinkSchema),
    defaultValues: {
      email: '',
    },
  });
  const {
    sendResetPasswordLinkQuery: {
      mutateAsync: resetPassword,
      isPending: isLoadingResetPassword,
      error: errorResetPassword,
      isError: isErrorResetPassword,
      isSuccess: isSuccessResetPassword,
    },
  } = useAuthContext();

  const handleSendResetPasswordLink = (data: SendResetPasswordLinkRequest) => {
    void (async () => {
      try {
        if (isLoadingResetPassword) return;

        await resetPassword({
          email: data.email,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <BrandCardSideContent className="w-screen h-screen bg-bg-light px-4 py-6">
      <Stack spacing={4} sx={{ maxWidth: '100%' }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h4" sx={{ fontWeight: 600, mb: 1 }}>
            {t('forgotPasswordPage.header')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('forgotPasswordPage.description')}
          </Typography>
        </Box>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit(handleSendResetPasswordLink)}>
          <Stack spacing={3}>
            {isSuccessResetPassword && (
              <Alert status="success" className="rounded-lg">
                {t('forgotPasswordPage.successInfo')}
              </Alert>
            )}
            {isErrorResetPassword && (
              <Alert status="error" className="rounded-lg">
                {errorResetPassword?.message}
              </Alert>
            )}
            <Controller
              control={control}
              name="email"
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <TextField
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  value={value}
                  onChange={onChange}
                  disabled={isSuccessResetPassword}
                  placeholder={t('placeholder.email')}
                  variant="outlined"
                />
              )}
            />
            <Button
              color="primary"
              className="btn-primary w-full rounded-full normal-case bg-[#4E7DF6] hover:bg-[#3D63C4] border-none"
              type="submit"
              disabled={isLoadingResetPassword || isSuccessResetPassword}
            >
              {t('button.sendResetPasswordLink')}
            </Button>
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Link to={Routes.LOGIN}>
              <Button color="ghost">{t('button.backToLogin')}</Button>
            </Link>
          </Box>
        </form>
      </Stack>
    </BrandCardSideContent>
  );
};

export default ForgotPasswordPage;
